import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Asset } from '@/types/asset';
import type {
  DisposalDetails,
  DisposalAllocation,
} from '@/types/disposal';
import { dataService } from '@/services/data-service';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DisposalDetailsStep } from './disposal-wizard-step1';
import { DisposalAllocationStep } from './disposal-wizard-step2';
import { DisposalCompletionDialog } from './disposal-completion-dialog';

interface DisposalWizardProps {
  readonly asset: Asset;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onComplete?: () => void;
}

export function DisposalWizard({ asset, open, onClose, onComplete }: DisposalWizardProps): React.ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  const [details, setDetails] = useState<DisposalDetails>({
    reference: '',
    date: new Date().toISOString().split('T')[0],
    disposalReasonId: '',
    year: new Date().getFullYear(),
    period: new Date().getMonth() + 1,
    notes: '',
    disposeOfMode: 'value',
    currentQuantity: asset.quantity || 1,
    disposalQuantity: undefined,
    depreciationHandling: undefined,
  });

  const [allocation, setAllocation] = useState<DisposalAllocation>({
    actualCost: 0,
    actualDepreciation: 0,
    historicalCost: 0,
    historicalDepreciation: 0,
    actualCostPercentage: 100,
    actualDepreciationPercentage: 100,
    historicalCostPercentage: 100,
    historicalDepreciationPercentage: 100,
    proceedsValue: 0,
  });

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
  };

  const handleComplete = async () => {
    try {
      const response = await dataService.submitDisposal({
        assetId: asset.id,
        reference: details.reference,
        disposalDate: details.date,
        disposalReasonId: details.disposalReasonId,
        year: details.year,
        period: details.period,
        notes: details.notes,
        disposeOfMode: details.disposeOfMode,
        disposalQuantity: details.disposalQuantity,
        depreciationHandling: details.depreciationHandling,
        allocation: allocation,
      });

      if (response.success) {
        toast.success('Disposal posted successfully');
        setShowCompletionDialog(true);
      } else {
        toast.error(response.message || 'Failed to complete disposal');
      }
    } catch (error: any) {
      console.error('Failed to submit disposal:', error);
      toast.error(error.message || 'Failed to complete disposal');
    }
  };

  const handleCompletionChoice = (viewTransactions: boolean) => {
    setShowCompletionDialog(false);
    onClose();
    
    if (viewTransactions) {
      // Navigate to Asset History (Enquiry page) filtered to the asset
      navigate(`/enquiry?assetIds=${asset.id}`);
    }
    
    if (onComplete) {
      onComplete();
    }
  };

  const handleDialogClose = () => {
    onClose();
    // Reset state
    setStep(1);
    setDetails({
      reference: '',
      date: new Date().toISOString().split('T')[0],
      disposalReasonId: '',
      year: new Date().getFullYear(),
      period: new Date().getMonth() + 1,
      notes: '',
      disposeOfMode: 'value',
      currentQuantity: asset.quantity || 1,
      disposalQuantity: undefined,
      depreciationHandling: undefined,
    });
    setAllocation({
      actualCost: 0,
      actualDepreciation: 0,
      historicalCost: 0,
      historicalDepreciation: 0,
      actualCostPercentage: 100,
      actualDepreciationPercentage: 100,
      historicalCostPercentage: 100,
      historicalDepreciationPercentage: 100,
      proceedsValue: 0,
    });
  };

  return (
    <>
      <Dialog open={open && !showCompletionDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="w-[600px] max-w-[600px] max-h-[90vh] overflow-y-auto p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">
              Disposal - {asset.code}
            </DialogTitle>
          </DialogHeader>

          {/* Wizard Steps Indicator */}
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="flex items-center flex-1">
              <div className={`flex items-center ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step >= 1 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground'}`}>
                  {step > 1 ? '✓' : '1'}
                </div>
                <span className="ml-2 text-sm font-medium">Disposal Details</span>
              </div>
              <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
            </div>
            
            <div className="flex items-center flex-1">
              <div className={`flex items-center ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step >= 2 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground'}`}>
                  {step > 2 ? '✓' : '2'}
                </div>
                <span className="ml-2 text-sm font-medium">Allocation</span>
              </div>
              <div className={`flex-1 h-0.5 mx-4 ${step >= 3 ? 'bg-primary' : 'bg-muted'}`}></div>
            </div>
            
            <div className={`flex items-center ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step >= 3 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground'}`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Review</span>
            </div>
          </div>

          {step === 1 && (
            <DisposalDetailsStep
              asset={asset}
              details={details}
              onDetailsChange={setDetails}
              onNext={handleNext}
              onCancel={handleDialogClose}
            />
          )}

          {step === 2 && (
            <DisposalAllocationStep
              asset={asset}
              details={details}
              allocation={allocation}
              onDetailsChange={setDetails}
              onAllocationChange={setAllocation}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Review & Confirm</h3>
                
                {/* Summary Panel */}
                <div className="rounded-lg border p-4 space-y-3 bg-muted/50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Asset</p>
                      <p className="font-medium">{asset.code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Reference</p>
                      <p className="font-medium">{details.reference}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(details.date).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Period</p>
                      <p className="font-medium">{details.year} / {details.period}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dispose Of</p>
                      <p className="font-medium capitalize">{details.disposeOfMode}</p>
                    </div>
                    {details.disposalQuantity && (
                      <div>
                        <p className="text-sm text-muted-foreground">Disposal Quantity</p>
                        <p className="font-medium">{details.disposalQuantity}</p>
                      </div>
                    )}
                    {details.depreciationHandling && (
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">Depreciation Handling</p>
                        <p className="font-medium capitalize">{details.depreciationHandling.replace('_', ' ')}</p>
                      </div>
                    )}
                  </div>
                  
                  {details.notes && (
                    <div>
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm">{details.notes}</p>
                    </div>
                  )}
                </div>

                {/* Allocation Summary */}
                <div className="rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Allocation Summary</h4>
                  {details.disposeOfMode === 'percentage' ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-4 text-sm font-medium border-b pb-2">
                        <div></div>
                        <div>Actual Cost %</div>
                        <div>Historical Cost %</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>Asset Cost</div>
                        <div>{allocation.actualCostPercentage?.toFixed(2)}%</div>
                        <div>{allocation.historicalCostPercentage?.toFixed(2)}%</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>Depreciation</div>
                        <div>{allocation.actualDepreciationPercentage?.toFixed(2)}%</div>
                        <div>{allocation.historicalDepreciationPercentage?.toFixed(2)}%</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t">
                        <div>Proceeds Value</div>
                        <div className="col-span-2">{asset.currency} {allocation.proceedsValue?.toFixed(2)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-4 text-sm font-medium border-b pb-2">
                        <div></div>
                        <div>Actual Cost</div>
                        <div>Historical Cost</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>Asset Cost</div>
                        <div>{asset.currency} {allocation.actualCost?.toFixed(2)}</div>
                        <div>{asset.currency} {allocation.historicalCost?.toFixed(2)}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>Depreciation</div>
                        <div>{asset.currency} {allocation.actualDepreciation?.toFixed(2)}</div>
                        <div>{asset.currency} {allocation.historicalDepreciation?.toFixed(2)}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t">
                        <div>Proceeds Value</div>
                        <div className="col-span-2">{asset.currency} {allocation.proceedsValue?.toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleComplete}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
                >
                  Complete Disposal
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DisposalCompletionDialog
        open={showCompletionDialog}
        onChoice={handleCompletionChoice}
      />
    </>
  );
}
